export type ProcessLogKind = 'analysis' | 'context' | 'thinking' | 'search' | 'action' | 'result' | 'error';
export type ProcessLogStatus = 'active' | 'completed' | 'error';

export interface ProcessLogEntry {
  id: string;
  kind: ProcessLogKind;
  status: ProcessLogStatus;
  title: string;
  detail?: string;
  timestamp?: number;
  /**
   * 🔴 M18: 内部去重键（仅供 processLogManager / tagParser 内部使用）。
   * 由 createProcessLog 创建后由调用方设置；finalizeProcessLogs 在持久化前会剥离此字段。
   * 不写入后端 metadata，不影响 UI 渲染。
   */
  dedupKey?: string;
}

export interface AttachmentItem {
  file?: File;
  name: string;
  type: string;
  size: number;
  thumbnailUrl?: string;
}

// P2-27: CollabStatusEntry 定义在 @/types/agent.ts

export interface MessageMetadata {
  pendingTaskId?: string;
  pendingNodeId?: string;
  taskStatus?: string;
  /** 批量任务 ID 数组（image2psd 等多图生成场景）。
   *  消息通过此数组匹配每个批量子任务的结果，逐步累加到 mediaData.results。 */
  batchTaskIds?: string[];
  [key: string]: unknown;
}

// 🔵 #26: Message 保留单一 interface（Discriminated Union 在大型代码库中改动过大）
// 但通过注释明确 type 与 *Data 字段的绑定关系，代码中应使用 `m.type === 'search' && m.searchData` 做类型守卫
export interface Message {
  id?: string;
  role: 'user' | 'model';
  text: string;
  introText?: string;
  /**
   * M16 语义说明：`type='generation_confirm'` 与 `ActiveTask.resultType='confirm'` 表达同一含义
   * （生成参数确认场景），但使用不同字符串。二者语义等价，区别在于使用阶段：
   * - `Message.type='generation_confirm'`：用于历史消息恢复（messageRestore._restoreGenerationConfirm）
   *   和最终消息落库（buildFinalizePayload 的 confirm 分支）。
   * - `ActiveTask.resultType='confirm'`：用于流式渲染期间的活跃任务状态判定（useStreamLifecycle 等）。
   * 新事件流走 `task_ask` 事件（TaskAskData.mode='generation_confirm'），但最终消息以
   * `type='generation_confirm'` 落库，保持与历史消息兼容。不修改 `Message.type` 值以避免破坏历史消息。
   *
   * P2-22：完整映射表见 task/types.ts 的 mapResultTypeToMessageType（F-M10 集中化）。
   */
  type?: 'text' | 'search' | 'workflow' | 'image' | 'video' | 'generation_confirm' | 'error';
  /** 仅 type==='search' 时应有值 */
  searchData?: any;
  /** 仅 type==='workflow' 时应有值 */
  workflowData?: any;
  /** 仅 type==='image'/'video' 时应有值 */
  mediaData?: any;
  attachments?: AttachmentItem[];
  references?: { id: string; thumbnailUrl?: string; title?: string }[];
  processLogs?: ProcessLogEntry[];
  /** 仅 type==='generation_confirm' 时应有值（生成参数，含模型/比例/分辨率等） */
  generationOptions?: any;
  /** 仅 type==='generation_confirm' 时应有值（待确认后重发的用户附件） */
  pendingAttachments?: any[];
  /** 仅 type==='generation_confirm' 时应有值（待确认后重发的引用节点） */
  pendingReferences?: any[];
  /** 仅 type==='generation_confirm' 时应有值（待确认后重发的用户文本） */
  pendingUserText?: string;
  /** 仅 type==='generation_confirm' 时应有值（待确认后重发的引用画布节点 ID） */
  pendingRefNodeIds?: string[];
  metadata?: MessageMetadata;
  isHistory?: boolean;
  /** P0-F2: 后端完成标记透传（0=流式占位未完成 / 1=已完成 / 2=中断）。
   * 仅历史恢复路径设置；未完成消息由 StreamingRow 展示，MessageList 过滤双渲染（根因 R5）。 */
  isComplete?: number;
  /** 任务流改版：新消息携带的任务快照 */
  task?: import('./task/types').ActiveTask | null;
}

export interface ChatHistoryEntry {
  id: string;
  topic: string;
  date: string;
  updatedAt: string;  // ISO 时间戳，用于排序
  messages: Message[];
}

/**
 * P1-18: 统一的意图信息结构化对象。
 *
 * 替代旧 store.currentIntent: string，使 store 与流式会话状态使用同一结构。
 * - name: 意图名（如 'chat' / 'search' / 'page_design' / 'generate_image' 等）
 * - confidence: 置信度 0-1
 * - source: 识别来源（如 'rule' / 'llm' / 'default'）
 * - params: 可选扩展参数
 */
export interface IntentInfo {
  name: string;
  confidence: number;
  source: string;
  params?: Record<string, any>;
}

export const FEATURE_GRIDS: Record<string, { rows: number; cols: number }> = {
  '多机位九宫格': { rows: 3, cols: 3 },
  '剧情推演四宫格': { rows: 2, cols: 2 },
  '25宫格连贯分镜': { rows: 5, cols: 5 },
  '电影级光影校正': { rows: 1, cols: 1 },
  '角色三视图生成': { rows: 1, cols: 3 },
  // P2-28: 统一为带空格版本（与 flow/nodes/parts/TopFunctionBar.tsx 用户可见标签一致）
  '画面推演 - 3秒后': { rows: 1, cols: 1 },
  '画面推演 - 5秒前': { rows: 1, cols: 1 },
};

export const PROCESS_KIND_LABELS: Record<ProcessLogKind, string> = {
  analysis: '分析',
  context: '上下文',
  thinking: '思考',
  search: '搜索',
  action: '执行',
  result: '结果',
  error: '错误',
};
